import React, { useCallback, useEffect, useState } from "react";
import * as Utils from "../utils";
import { api, ApiError } from "../api/client";
import { useMessage } from "../components/MessageProvider";
import RichiesteNuovo from "./RichiesteNuovo";
import RichiesteDettaglio from "./RichiesteDettaglio";

const IconBack = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
  </svg>
);

function statoColor(stato) {
  switch ((stato || "").toLowerCase()) {
    case "respinto":    return "bg-red-400";
    case "approvato":   return "bg-green-500";
    case "da confermare": return "bg-yellow-300";
    default:            return "bg-gray-300";
  }
}

function RichiesteStorico({ user, setUser, isOnline, onSetTopBarAction }) {
  const [view, setView] = useState("lista"); // "lista" | "nuovo" | "dettaglio"
  const [richieste, setRichieste] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dettaglio, setDettaglio] = useState(null);
  const [loadingDettaglio, setLoadingDettaglio] = useState(false);
  const { showMessage, showQuestion } = useMessage();

  const fetchRichieste = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.post("/api/getRichieste", { session_token: user.token });
      setRichieste(data?.richieste || []);
    } catch {
      setRichieste([]);
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    if (user?.id) fetchRichieste();
  }, [user?.id, fetchRichieste]);

  // TopBar action — cambia in base alla view corrente
  useEffect(() => {
    if (!onSetTopBarAction) return;
    if (view === "lista") {
      onSetTopBarAction({
        icon: <IconPlus />,
        label: "Nuova richiesta",
        onClick: () => setView("nuovo"),
      });
    } else {
      onSetTopBarAction({
        icon: <IconBack />,
        label: "Indietro",
        onClick: () => {
          if (view === "dettaglio") fetchRichieste();
          setView("lista");
          setDettaglio(null);
        },
      });
    }
    return () => onSetTopBarAction(null);
  }, [onSetTopBarAction, view, fetchRichieste]);

  const handleSelectRichiesta = async (r) => {
    setDettaglio(null);
    setLoadingDettaglio(true);
    setView("dettaglio");
    try {
      const data = await api.post("/api/richiesta", { session_token: user.token, richiesta_id: r.id });
      setDettaglio(data?.richiesta || data);
    } catch {
      setDettaglio(null);
    } finally {
      setLoadingDettaglio(false);
    }
  };

  const handleAnnullaRichiesta = async () => {
    if (!dettaglio?.id) return;
    const confirmed = await showQuestion({
      title: "Elimina richiesta",
      message: "Sei sicuro di voler eliminare questa richiesta?",
      confirmLabel: "Sì, elimina",
      cancelLabel: "Annulla",
    });
    if (!confirmed) return;
    try {
      await api.post("/api/richiesta/annulla", { session_token: user.token, richiesta_id: dettaglio.id });
      await showMessage({ title: "Richiesta eliminata", message: "Richiesta annullata con successo.", type: "success", timeout: 3 });
      setView("lista");
      setDettaglio(null);
      fetchRichieste();
    } catch (e) {
      if (e instanceof ApiError && !e.isNetworkError) {
        await showMessage({ title: "Errore", message: e.body?.error || e.message || "Errore durante l'annullamento.", type: "error", timeout: 0 });
      } else {
        await showMessage({ title: "Errore", message: "Errore di rete durante l'annullamento.", type: "error", timeout: 0 });
      }
    }
  };

  // Vista: nuovo form
  if (view === "nuovo") {
    return (
      <RichiesteNuovo
        user={user}
        setUser={setUser}
        isOnline={isOnline}
        onDone={() => {
          setView("lista");
          fetchRichieste();
        }}
      />
    );
  }

  // Vista: dettaglio
  if (view === "dettaglio") {
    if (loadingDettaglio) {
      return (
        <div style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-lg mx-auto mt-4 text-center text-gray-500">
            Caricamento dettaglio...
          </div>
        </div>
      );
    }
    if (!dettaglio) {
      return (
        <div style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-lg mx-auto mt-4 text-center text-red-500">
            Impossibile caricare il dettaglio della richiesta.
          </div>
        </div>
      );
    }
    return (
      <RichiesteDettaglio
        richiesta={dettaglio}
        onClose={() => { setView("lista"); fetchRichieste(); }}
        onAnnullaRequest={handleAnnullaRichiesta}
      />
    );
  }

  // Vista: lista (default)
  if (!isOnline) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-4 max-w-2xl mx-auto mt-4 w-full text-center text-orange-600">
        Sei offline. Lo storico richieste non è disponibile.
      </div>
    );
  }

  return (
    <div style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="bg-white rounded-xl shadow-lg p-4 max-w-2xl mx-auto mt-4 w-full">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Caricamento...</div>
        ) : richieste.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Nessuna richiesta trovata.</div>
        ) : (
          <table className="w-full text-base">
            <thead>
              <tr className="border-b">
                <th className="font-medium text-primary text-sm sm:text-base font-bold text-left">Tipo</th>
                <th className="font-medium text-primary text-sm sm:text-base font-bold text-center">Inizio</th>
                <th className="font-medium text-primary text-sm sm:text-base font-bold text-center">Fine</th>
                <th className="font-medium text-primary text-sm sm:text-base font-bold text-center">Stato</th>
              </tr>
            </thead>
            <tbody>
              {richieste.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    className="cursor-pointer active:bg-blue-50 transition"
                    onClick={() => handleSelectRichiesta(r)}
                    title="Vedi dettagli richiesta"
                  >
                    <td className="pt-2 pb-0 text-left">{r.tipologia || r.tipo || "-"}</td>
                    <td className="pt-2 pb-0 text-center">{Utils.formatItalianDate(r.dal, false, true)}</td>
                    <td className="pt-2 pb-0 text-center">{Utils.formatItalianDate(r.al, false, true)}</td>
                    <td className="pt-2 pb-0 text-center align-middle">
                      <span
                        className={"inline-block w-4 h-4 rounded-full mt-2 " + statoColor(r.stato_richiesta)}
                        title={r.stato_richiesta}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="py-0 px-4 text-gray-700 text-left italic text-xs">
                      {r.note ? r.note : " "}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="border-b p-0" />
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default RichiesteStorico;
